// Persistent impersonation banner. It is always rendered while a session is
// impersonating, states both the actor and the effective account, and exposes an
// always-reachable stop action. Other admin operations remain hidden elsewhere.

import { useState, type ReactNode } from 'react';
import { Alert, Button, Group, LoadingOverlay, Stack, Text } from '@mantine/core';
import { IconUserSearch } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/i18n/I18nProvider';
import { useSession } from '@/session/SessionProvider';
import { stopImpersonation } from '@/api/endpoints';
import { ApiError } from '@/api/errors';
import { ErrorState } from '@/components/primitives';
import { queryKeys } from '@/query/keys';

export function ImpersonationBanner(): ReactNode {
  const { state, isImpersonating, refresh, broadcastChange } = useSession();
  const t = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>(null);

  const mutation = useMutation({
    mutationFn: () => stopImpersonation(),
    onMutate: () => setError(null),
    onError: (mutationError) => {
      setError(mutationError);
      // A rejected stop usually means the session context is stale; re-read it
      // rather than guessing at the new state.
      if (mutationError instanceof ApiError && mutationError.isForbidden) {
        void refresh();
      }
    },
    onSuccess: async () => {
      // Disable account-scoped mutations during the transition by removing the
      // old context data, then refetch identity and let queries repopulate.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === 'account' });
      await queryClient.invalidateQueries({ queryKey: queryKeys.session });
      await refresh();
      broadcastChange();
    },
  });

  if (!isImpersonating || state.status !== 'authenticated') return null;

  const session = state.session;

  return (
    <Alert
      color="clay"
      variant="filled"
      icon={<IconUserSearch size={20} aria-hidden />}
      // Impersonation is a security-relevant state change, so it is announced
      // assertively. Mantine names the alert from its title, which already
      // states both the actor and the effective account.
      role="alert"
      mb="md"
      title={t('impersonation.banner', {
        effective: session.userId,
        actor: session.actorUserId,
      })}
    >
      <Stack gap="xs">
        {error ? <ErrorState error={error} /> : null}
        <Group justify="space-between" align="center" wrap="wrap">
          <Text size="sm">{t('admin.blockedWhileImpersonating')}</Text>
          <Button
            variant="white"
            color="clay"
            size="xs"
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {t('impersonation.stop')}
          </Button>
        </Group>
      </Stack>
      {mutation.isPending ? <LoadingOverlay visible loaderProps={{ size: 'xs' }} /> : null}
    </Alert>
  );
}
